import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useSortableTable, SortableTh } from "@/lib/useSortableTable";

/**
 * DailySummaryByTeamCard
 * Same shape as DailySummaryCard but rows are (date, team) — lets ops leads
 * compare team performance side by side for the selected date range.
 */
export default function DailySummaryByTeamCard({ params, title, subtitle }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const { rows: rowsSorted, thProps } = useSortableTable(rows, {
    initialKey: "date", initialDir: "desc",
    accessors: {
      date: (r) => r.date || "",
      team_name: (r) => (r.team_name || "").toLowerCase(),
    },
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const q = new URLSearchParams(params).toString();
        const { data } = await api.get(`/ops/analytics/daily-summary-by-team${q ? `?${q}` : ""}`);
        if (alive) setRows(data.rows || []);
      } catch (e) {
        if (alive) setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [JSON.stringify(params)]);

  return (
    <div className="glass-card p-4" data-testid="daily-summary-by-team">
      <div className="mb-3">
        <div className="font-outfit font-semibold text-slate-900">{title || "Daily summary by team"}</div>
        {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-slate-500 text-center py-6">No data for the selected filters.</div>
      ) : (
        <div className="overflow-auto max-h-[70vh]">
        <table className="w-full text-sm">
          <thead data-sticky>
            <tr className="text-slate-500 text-xs uppercase">
              <SortableTh {...thProps("date", "left")}>Date</SortableTh>
              <SortableTh {...thProps("team_name", "left")}>Team</SortableTh>
              <SortableTh {...thProps("active_agents")}>Agents</SortableTh>
              <SortableTh {...thProps("total_calls")}>Total calls</SortableTh>
              <SortableTh {...thProps("unique_users_called")}>Users called</SortableTh>
              <SortableTh {...thProps("fresh_calls")}>Fresh</SortableTh>
              <SortableTh {...thProps("followup_calls")}>Follow-ups</SortableTh>
              <SortableTh {...thProps("unique_users_contactable")}>Contactable</SortableTh>
              <SortableTh {...thProps("contactable_pct")} title="Contactable ÷ Users called">Contact %</SortableTh>
              <SortableTh {...thProps("unique_users_converted")}>Converted</SortableTh>
              <SortableTh {...thProps("unique_users_total_converted")} className="bg-slate-50" title="Total across all credit channels.">Total conv</SortableTh>
              <SortableTh {...thProps("conversion_of_contactable_pct")} title="Converted ÷ Contactable">Conv % (contactable)</SortableTh>
              <SortableTh {...thProps("unique_users_sms")} title="SMS/voicemail credited deposits.">SMS conv</SortableTh>
              <SortableTh {...thProps("unique_users_wn")} title="'Wrong number' rows that later deposited.">WN → Conv</SortableTh>
              <SortableTh {...thProps("unique_users_invalid")} title="'Invalid number' rows that later deposited.">Inv → Conv</SortableTh>
              <SortableTh {...thProps("conversion_pct")} title="Converted ÷ Users called (legacy).">Conv % (dialled)</SortableTh>
              <SortableTh {...thProps("deposits_after")}>Deposits after</SortableTh>
            </tr>
          </thead>
          <tbody>
            {rowsSorted.map((r, i) => (
              <tr key={`${r.date}-${r.team_id || i}`} className="border-t border-white/60"
                  data-testid={`by-team-row-${r.date}-${r.team_id || "none"}`}>
                <td className="py-1.5 px-2 font-medium">{r.date}</td>
                <td className="py-1.5 px-2 font-medium text-slate-700">{r.team_name}</td>
                <td className="py-1.5 px-2 text-right">{r.active_agents}</td>
                <td className="py-1.5 px-2 text-right">{(r.total_calls || 0).toLocaleString()}</td>
                <td className="py-1.5 px-2 text-right">{(r.unique_users_called || 0).toLocaleString()}</td>
                <td className="py-1.5 px-2 text-right text-emerald-700">{(r.fresh_calls || 0).toLocaleString()}</td>
                <td className="py-1.5 px-2 text-right text-blue-700">{(r.followup_calls || 0).toLocaleString()}</td>
                <td className="py-1.5 px-2 text-right">{(r.unique_users_contactable || 0).toLocaleString()}</td>
                <td className="py-1.5 px-2 text-right">{(r.contactable_pct ?? 0).toFixed(1)}%</td>
                <td className="py-1.5 px-2 text-right font-semibold">{(r.unique_users_converted || 0).toLocaleString()}</td>
                <td className="py-1.5 px-2 text-right bg-slate-50 font-semibold text-slate-900">
                  {(r.unique_users_total_converted || 0).toLocaleString()}
                  {r.unique_users_called > 0 && (
                    <span className="text-slate-400 text-[10px] ml-1">({(r.total_conversion_pct ?? 0).toFixed(1)}%)</span>
                  )}
                </td>
                <td className="py-1.5 px-2 text-right font-semibold text-emerald-700">{(r.conversion_of_contactable_pct ?? 0).toFixed(1)}%</td>
                <td className="py-1.5 px-2 text-right text-sky-700">
                  {(r.unique_users_sms || 0).toLocaleString()}
                  {r.unique_users_called > 0 && (
                    <span className="text-slate-400 text-[10px] ml-1">({(r.sms_conversion_pct ?? 0).toFixed(1)}%)</span>
                  )}
                </td>
                <td className={`py-1.5 px-2 text-right ${(r.unique_users_wn || 0) > 0 ? "text-amber-700 font-medium" : "text-slate-400"}`}>{(r.unique_users_wn || 0).toLocaleString()}</td>
                <td className={`py-1.5 px-2 text-right ${(r.unique_users_invalid || 0) > 0 ? "text-amber-700 font-medium" : "text-slate-400"}`}>{(r.unique_users_invalid || 0).toLocaleString()}</td>
                <td className="py-1.5 px-2 text-right text-slate-500">{(r.conversion_pct ?? 0).toFixed(1)}%</td>
                <td className="py-1.5 px-2 text-right">{(r.deposits_after || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}
